import { FilePlus2, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Label } from '@/components/ui/label'
import { Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { apiErrorMessage } from '@/i18n'
import { useTemplates } from '../api'
import { useRequestDocuments } from '../followupApi'

/** Personel(ler)den yayımlanmış bir şablondaki belgeyi son tarih ve notla ister (ek / sonradan istenen belge). */
export function RequestDocumentModal({ employeeIds, templateId, onDone }: { employeeIds: string[]; templateId?: string; onDone: () => void }) {
  const { t } = useTranslation()
  const templates = useTemplates()
  const request = useRequestDocuments()
  const [tpl, setTpl] = useState(templateId ?? '')
  const [due, setDue] = useState('')
  const [note, setNote] = useState('')
  const options = (templates.data ?? []).filter((x) => x.status === 'yayinda').map((x) => ({ value: x.id, label: x.ad }))

  const onSubmit = () =>
    request.mutate(
      { templateId: tpl, employeeIds, dueAt: due, note },
      {
        onSuccess: (r) => {
          const created = Number(r?.created) || 0
          const skipped = Number(r?.skipped) || 0
          toast.success(t('hr.belgeler.request.done', { created, skipped }))
          onDone()
        },
        onError: (e) => toast.error(apiErrorMessage(e)),
      },
    )

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onDone()}
      icon={<FilePlus2 />}
      title={t('hr.belgeler.request.title')}
      description={t('hr.belgeler.request.hint', { count: employeeIds.length })}
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit()
      }}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={!tpl || request.isPending}>
            {request.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('hr.belgeler.request.submit')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="rq-tpl">{t('hr.belgeler.request.template')}</Label>
          <Select id="rq-tpl" value={tpl} onChange={setTpl} searchable options={options} placeholder={t('hr.belgeler.request.templatePlaceholder')} disabled={!!templateId} />
          {!options.length && !templates.isLoading ? <p className="text-xs text-muted-foreground">{t('hr.belgeler.request.noTemplates')}</p> : null}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="rq-due">{t('hr.belgeler.request.due')}</Label>
          <DateField id="rq-due" value={due} onChange={setDue} min={new Date().toISOString().slice(0, 10)} />
          <p className="text-xs text-muted-foreground">{t('hr.belgeler.request.dueHint')}</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="rq-note">{t('hr.belgeler.request.note')}</Label>
          <Textarea id="rq-note" rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('hr.belgeler.request.notePlaceholder')} />
        </div>
      </div>
    </Modal>
  )
}
